import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CheckCircle2,
  Edit,
  Eye,
  Lock as LockIcon,
  Plus,
  RefreshCw,
  Trash2,
  User,
  XCircle,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { RootState } from '../../../store';
import { useAppSelector } from '../../../hooks';
import { selectUser, selectUserPermissions } from '../../user/profileSlice';
import { useGetRoleListQuery } from '../../roleManagement/role/roleApi';
import {
  IconButton,
  type ActionMenuItem,
  UniversalModuleView,
  type FilterFieldConfig,
} from '../../../ui-components/listing';
import { formatTimestampToDateTime } from '../../../utils/common';

import { useDeleteAdminMutation, useLazyGetAdminByIdQuery } from './adminApi';
import { setRoleData } from './adminSlice';

import { AdminRole } from './model';
import buildAdminTableCells from './components/AdminTableRow';

import type { Admin } from './model/admin';
import type { ColDef } from 'ag-grid-community';
import { API_CONSTANTS } from '../../../utils/constants';
import AdminDetailsModal from './components/AdminDetailsModal';
import AdminFormModal from './AdminFormModal';

const formatDate = (timestamp: number | string | undefined) => {
  return timestamp ? formatTimestampToDateTime(timestamp) : '';
};

const formatDateTime = (timestamp: number | string | undefined) => {
  return timestamp ? formatTimestampToDateTime(timestamp) : '';
};

export interface AdminsPageProps {
  initialModal?: { mode: 'create' | 'edit'; adminId?: string };
  modalReturnTo?: string;
}

const AdminsPage: React.FC<AdminsPageProps> = ({
  initialModal,
  modalReturnTo,
}) => {
  const [deleteAdmin] = useDeleteAdminMutation();
  const [
    fetchAdminDetails,
    { data: adminDetails, isFetching: isDetailLoading },
  ] = useLazyGetAdminByIdQuery();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: roleData } = useGetRoleListQuery();
  const permissions = useAppSelector(selectUserPermissions) ?? [];
  const user = useAppSelector(selectUser);
  const roleCode =
    typeof user?.groupCode === 'string'
      ? user.groupCode.trim().toUpperCase()
      : '';

  const [refreshToken, setRefreshToken] = useState(0);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailAdmin, setDetailAdmin] = useState<Admin | null>(null);
  const [isAdminFormOpen, setIsAdminFormOpen] = useState(Boolean(initialModal));
  const [adminFormMode, setAdminFormMode] = useState<'create' | 'edit'>(
    initialModal?.mode ?? 'create'
  );
  const [adminFormId, setAdminFormId] = useState<string | undefined>(
    initialModal?.adminId
  );

  useEffect(() => {
    if (roleData) {
      dispatch(setRoleData(roleData as any));
    }
  }, [roleData, dispatch]);

  useEffect(() => {
    if (!initialModal) return;
    setIsAdminFormOpen(true);
    setAdminFormMode(initialModal.mode);
    setAdminFormId(initialModal.adminId);
  }, [initialModal]);

  const roles = useSelector(
    (state: RootState) => state.admin.records as AdminRole[]
  );

  const can = useCallback(
    (code: string) => roleCode === 'HBADMIN' || permissions.includes(code),
    [permissions, roleCode]
  );

  const canAdd = can('admin_add');
  const canView = can('admin_view');
  const canEdit = can('admin_update');
  const canDelete = can('admin_delete');

  const resolveRoleLabel = useCallback((row: Admin) => {
    if (row.groupNames) return row.groupNames;
    if (Array.isArray(row.groups) && row.groups.length) {
      return row.groups
        .map((group) => group.groupName)
        .filter(Boolean)
        .join(', ');
    }
    return row.groupName || '-';
  }, []);

  const handleDelete = useCallback(
    async (adminId: number) => {
      try {
        await deleteAdmin(adminId).unwrap();
        setRefreshToken((prev) => prev + 1);
      } catch (error) {
        console.error(error);
      }
    },
    [deleteAdmin]
  );

  const handleViewDetails = useCallback(
    (admin: Admin) => {
      if (!canView) return;
      setDetailAdmin(admin);
      setIsDetailModalOpen(true);
      if (admin.adminId) {
        fetchAdminDetails(admin.adminId);
      }
    },
    [canView, fetchAdminDetails]
  );

  const openCreateAdmin = useCallback(() => {
    setAdminFormMode('create');
    setAdminFormId(undefined);
    setIsAdminFormOpen(true);
  }, []);

  const openEditAdmin = useCallback((adminId?: number) => {
    if (!adminId) return;
    setAdminFormMode('edit');
    setAdminFormId(String(adminId));
    setIsAdminFormOpen(true);
  }, []);

  const adminColumns = useMemo<ColDef<Admin>[]>(
    () => [
      {
        headerName: 'Name',
        field: 'name',
        sortable: true,
        minWidth: 150,
        cellClass: 'min-w-0 truncate',
      },
      {
        headerName: 'Role',
        field: 'groupName',
        sortable: false,
        minWidth: 150,
        cellClass: 'min-w-0 truncate',
        valueGetter: (params) =>
          params.data?.groupNames || params.data?.groupName || '-',
      },
      {
        headerName: 'Email',
        field: 'email',
        sortable: true,
        minWidth: 200,
        cellClass: 'min-w-0 truncate',
      },
      {
        headerName: 'Status',
        field: 'status',
        sortable: false,
        minWidth: 100,
      },
      {
        headerName: 'Added date',
        field: 'addedDate',
        sortable: true,
        minWidth: 150,
        cellClass: 'min-w-0 truncate',
        valueFormatter: (params) => formatDate(params.value as number),
      },
      {
        headerName: 'Site name',
        field: 'siteName',
        sortable: true,
        minWidth: 150,
        cellClass: 'min-w-0 truncate',
      },
      {
        headerName: 'Building name',
        field: 'buildingName',
        sortable: true,
        minWidth: 150,
        cellClass: 'min-w-0 truncate',
      },
      {
        headerName: 'Floor name',
        field: 'floorName',
        sortable: true,
        minWidth: 150,
        cellClass: 'min-w-0 truncate',
      },
      {
        headerName: 'Actions',
        field: '__actions',
        sortable: false,
        width: 80,
        headerClass: 'justify-end text-right',
        cellClass: 'justify-end text-right',
      },
    ],
    []
  );

  const buildActions = useCallback(
    (row: Admin) => {
      const actions: ActionMenuItem[] = [];
      if (canView) {
        actions.push({
          label: 'View Details',
          icon: Eye,
          onClick: () => handleViewDetails(row),
        });
      }
      if (canEdit) {
        actions.push({
          label: 'Edit',
          icon: Edit,
          onClick: () => openEditAdmin(row.adminId),
        });
      }
      if (canDelete) {
        actions.push({
          label: 'Delete',
          icon: Trash2,
          tone: 'danger',
          onClick: () => handleDelete(row.adminId),
        });
      }
      return actions;
    },
    [
      canDelete,
      canEdit,
      canView,
      handleDelete,
      handleViewDetails,
      openEditAdmin,
    ]
  );

  const renderTableRow = useCallback(
    (row: Admin) =>
      buildAdminTableCells({
        name: row.name,
        email: row.email,
        roleLabel: resolveRoleLabel(row),
        status: row.status,
        addedDateLabel: formatDate(row.addedDate),
        avatarUrl: row.profileImageUrl,
        siteName: row.siteName,
        buildingName: row.buildingName,
        floorName: row.floorName,
        actions: buildActions(row),
      }),
    [buildActions, resolveRoleLabel]
  );

  const filterConfig = useMemo<FilterFieldConfig[]>(
    () => [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: 'Active', label: 'Active' },
          { value: 'Inactive', label: 'Inactive' },
          { value: 'Blocked', label: 'Blocked' },
        ],
      },
      {
        key: 'groupId',
        label: 'Role',
        type: 'select',
        options: Array.isArray(roles)
          ? roles.map((role) => ({
              value: role.id,
              label: role.name,
            }))
          : [],
      },
      {
        key: 'startDate',
        label: 'Start Date',
        type: 'date',
      },
      {
        key: 'endDate',
        label: 'End Date',
        type: 'date',
      },
    ],
    [roles]
  );

  const detailData = useMemo(() => {
    const apiData = (adminDetails as { data?: Admin })?.data;
    if (!apiData && !detailAdmin) return null;
    return {
      ...detailAdmin,
      ...apiData,
    } as Admin;
  }, [adminDetails, detailAdmin]);

  return (
    <>
      <UniversalModuleView<Admin>
        moduleKey="admins"
        title="Admin List"
        apiEndpoint={API_CONSTANTS.ADMIN.ADMIN_LIST_URL}
        columnsConfig={adminColumns}
        rowKey={(row) => row.adminId}
        tableRowRenderer={renderTableRow}
        refreshToken={refreshToken}
        filterConfig={filterConfig}
        primaryAction={
          canAdd
            ? {
                label: 'Add Admin',
                icon: Plus,
                onClick: openCreateAdmin,
              }
            : undefined
        }
        extraActions={({ reset }) => (
          <>
            <IconButton icon={RefreshCw} title="Refresh" onClick={reset} />
          </>
        )}
        searchPlaceholder="Search admins by name or email..."
        initialPageSize={10}
        note={
          <>
            <strong>Note:</strong> Toggle user status between Active and
            Inactive. Inactive users will be blocked from logging in with the
            message: "Your account has been locked by admin." Click on any row
            to view detailed user information.
          </>
        }
      />

      <AdminDetailsModal
        isOpen={isDetailModalOpen}
        isLoading={isDetailLoading}
        name={detailData?.name}
        email={detailData?.email}
        roleLabel={detailData ? resolveRoleLabel(detailData) : '-'}
        dialCode={detailData?.dialCode}
        phoneNumber={detailData?.phoneNumber}
        status={detailData?.status}
        avatarUrl={detailData?.profileImageUrl}
        username={detailData?.userName ?? detailData?.username}
        designationName={detailData?.designationName}
        departementName={detailData?.departementName}
        subDepartementName={detailData?.subDepartementName}
        joiningDate={detailData?.joiningDate}
        immediateSupervisorID={detailData?.immediateSupervisorID}
        immediateSupervisorName={detailData?.immediateSupervisorName}
        lineManagerID={detailData?.lineManagerID}
        lineManagerName={detailData?.lineManagerName}
        hodId={detailData?.hodId}
        hodName={detailData?.hodName}
        siteName={detailData?.siteName}
        buildingName={detailData?.buildingName}
        floorName={detailData?.floorName}
        createdDateLabel={
          detailData?.addedDate ? formatDate(detailData.addedDate) : ''
        }
        lastLoginLabel={
          detailData?.lastAccess ? formatDateTime(detailData.lastAccess) : ''
        }
        onClose={() => {
          setIsDetailModalOpen(false);
          setDetailAdmin(null);
        }}
      />

      <AdminFormModal
        isOpen={isAdminFormOpen}
        mode={adminFormMode}
        adminId={adminFormId}
        onClose={() => {
          setIsAdminFormOpen(false);
          setAdminFormId(undefined);
          if (modalReturnTo) {
            navigate(modalReturnTo);
          }
        }}
        onSuccess={() => {
          setRefreshToken((prev) => prev + 1);
        }}
      />
    </>
  );
};

export default AdminsPage;
